import React, { useCallback, useEffect, useState } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import PanelSummaryCard from "../common/PanelSummaryCard";
import LabPanelQuestionFields from "../common/LabPanelQuestionFields";
import StartServiceElement from "./StartServiceElement";
import {
  getPanelQuestions,
  LabPanelQuestion,
} from "../../utils/labPanelQuestions";

interface LabPanelEditorProps {
  panelConfig: any;
  panelData: any;
  onPanelConfigChange: (updatedConfig: any) => void;
  forceSmallScreen?: boolean;
  type?: "logicBuilder" | "prescriptionBuilder";
}

const LabPanelEditor = ({
  panelConfig,
  panelData,
  onPanelConfigChange,
  forceSmallScreen = false,
  type = "logicBuilder",
}: LabPanelEditorProps) => {
  const theme: Theme = useTheme();
  const [isSmallScreen, setIsSmallScreen] = useState(
    forceSmallScreen || window.innerWidth < 1100
  );

  useEffect(() => {
    if (forceSmallScreen) {
      setIsSmallScreen(true);
      return;
    }

    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1100);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [forceSmallScreen]);

  // Create a synthetic service object for StartServiceElement
  const syntheticService =
    type === "prescriptionBuilder"
      ? {
          startDate: panelConfig.startDate ?? null,
        }
      : {
          startOffset: panelConfig.startOffset ?? 0,
          startOffsetUnit: panelConfig.startOffsetUnit ?? 4,
        };

  const handleServiceChange = useCallback(
    (updatedService: any) => {
      if (type === "prescriptionBuilder") {
        onPanelConfigChange({
          ...panelConfig,
          startDate: updatedService.startDate,
        });
      } else {
        onPanelConfigChange({
          ...panelConfig,
          startOffset: updatedService.startOffset,
          startOffsetUnit: updatedService.startOffsetUnit,
        });
      }
    },
    [panelConfig, onPanelConfigChange, type]
  );

  // The lab's order-entry questions. In the prescription builder they are
  // answered here, in inputs under the start date: the answers live on each
  // question's `userAnswer`, on `panelConfig.questions`, and the full question
  // objects go into the prescription payload. The logic builder only informs
  // (the summary card's questions box) — a logic is a template, nobody can
  // answer for a client yet. Until the first answer is typed the config has
  // no `questions` of its own, so the panel's are shown.
  const isPrescription = type === "prescriptionBuilder";
  const questions: LabPanelQuestion[] = isPrescription
    ? Array.isArray(panelConfig.questions) && panelConfig.questions.length > 0
      ? panelConfig.questions
      : getPanelQuestions(panelData)
    : [];

  const handleQuestionsChange = (updated: LabPanelQuestion[]) => {
    onPanelConfigChange({ ...panelConfig, questions: updated });
  };

  return (
    <div style={styles.container(theme)}>
      <div style={styles.content}>
        {/* Panel header */}
        <div style={styles.panelHeader}>
          <div style={styles.panelTitle(theme)}>
            {panelData?.name || "Lab Panel"}
          </div>
          <div style={styles.panelType(theme)}>Lab panel</div>
        </div>

        {/* Start offset / Start date in responsive grid */}
        <div style={styles.attributesContainer(isSmallScreen)}>
          <StartServiceElement
            service={syntheticService}
            onServiceChange={handleServiceChange}
            type={type}
          />
        </div>
        {/* Order-entry questions — prescription builder only */}
        <LabPanelQuestionFields
          questions={questions}
          onChange={handleQuestionsChange}
          panel={panelData}
          singleColumn={isSmallScreen}
        />
        {/* Panel summary */}
        {panelData && (
          <div>
            <div style={styles.panelSummaryContainerTitle(theme)}>
              Panel summary
            </div>
            <div style={styles.panelSummaryContainer(theme)}>
              <PanelSummaryCard
                panel={panelData}
                showQuestions={!isPrescription}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LabPanelEditor;

const styles = {
  container: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
  }),
  content: {
    padding: "16px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
  },
  panelHeader: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  panelTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  panelType: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  attributesContainer: (isSmallScreen: boolean) => ({
    display: "grid",
    gridTemplateColumns: isSmallScreen ? "1fr" : "repeat(2, 1fr)",
    gap: "16px",
  }),
  panelSummaryContainerTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "17px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    paddingBottom: "8px",
  }),
  panelSummaryContainer: (theme: Theme) => ({
    padding: "12px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
  }),
};
